import type { InstrumentDefinition } from "./instrument";
import type { Cable, CustomSetting, PerformanceState } from "./patch";

export type PerformanceTransitionItem = {
  id: string;
  kind: "control" | "cable" | "setting";
  action: "set" | "add" | "remove";
  targetId: string;
  revealTargetId?: string;
  sectionId?: string;
  label: string;
  detail: string;
  colour?: string;
};

type CurrentPerformanceSnapshot = {
  controlValues: Record<string, number>;
  cables: Cable[];
  customSettings: CustomSetting[];
};

function endpointSignature(endpoint: Cable["endpointA"]) {
  return endpoint.kind === "port"
    ? `port:${endpoint.nodeId}:${endpoint.portId}`
    : `loose:${endpoint.x}:${endpoint.y}:${endpoint.label ?? ""}`;
}

function cableSignature(cable: Cable) {
  return [endpointSignature(cable.endpointA), endpointSignature(cable.endpointB)].sort().join("|");
}

function firstPortId(cable: Cable) {
  if (cable.endpointA.kind === "port") return cable.endpointA.portId;
  if (cable.endpointB.kind === "port") return cable.endpointB.portId;
  return undefined;
}

function pairCableRoutes(current: Cable[], target: Cable[]) {
  const remainingTarget = [...target];
  const removals: Cable[] = [];

  for (const cable of current) {
    const signature = cableSignature(cable);
    const matchIndex = remainingTarget.findIndex(
      (candidate) => cableSignature(candidate) === signature,
    );
    if (matchIndex >= 0) remainingTarget.splice(matchIndex, 1);
    else removals.push(cable);
  }

  return { additions: remainingTarget, removals };
}

export function describePerformanceControlValue(
  definition: InstrumentDefinition,
  controlId: string,
  values: Record<string, number>,
) {
  const control = definition.controls.find((candidate) => candidate.id === controlId);
  if (!control) return "Unknown";
  const value = values[control.id] ?? control.defaultValue;
  if (control.options) {
    return control.options[Math.round(value * (control.options.length - 1))]?.label ?? "—";
  }
  if (control.type === "button") return value >= 0.5 ? "Active" : "Inactive";
  return `${Math.round(value * 100)}%`;
}

export function createPerformanceTransitionItems(
  definition: InstrumentDefinition,
  current: CurrentPerformanceSnapshot,
  target: PerformanceState,
): PerformanceTransitionItem[] {
  const portNames = new Map(definition.ports.map((port) => [port.id, port.label]));
  const describeEndpoint = (endpoint: Cable["endpointA"]) =>
    endpoint.kind === "loose"
      ? (endpoint.label ?? "Loose end")
      : (portNames.get(endpoint.portId) ?? endpoint.portId);
  const describeCable = (cable: Cable) =>
    `${describeEndpoint(cable.endpointA)} → ${describeEndpoint(cable.endpointB)}`;

  const { additions, removals } = pairCableRoutes(current.cables, target.cableAssemblies);
  const controlItems = definition.controls.flatMap((control) => {
    const currentValue = current.controlValues[control.id] ?? control.defaultValue;
    const targetValue = target.controlValues[control.id] ?? control.defaultValue;
    if (Math.abs(currentValue - targetValue) <= 0.0001) return [];
    return [
      {
        id: `transition:control:${control.id}`,
        kind: "control" as const,
        action: "set" as const,
        targetId: control.id,
        sectionId: control.sectionId,
        label: control.label,
        detail: `Change from ${describePerformanceControlValue(definition, control.id, current.controlValues)} to ${describePerformanceControlValue(definition, control.id, target.controlValues)}.`,
      },
    ];
  });

  const currentSettings = new Map(current.customSettings.map((setting) => [setting.id, setting]));
  const targetSettings = new Map(target.customSettings.map((setting) => [setting.id, setting]));
  const settingRemovals: PerformanceTransitionItem[] = current.customSettings.flatMap(
    (setting): PerformanceTransitionItem[] =>
      targetSettings.has(setting.id)
        ? []
        : [
            {
              id: `transition:setting-remove:${setting.id}`,
              kind: "setting",
              action: "remove",
              targetId: setting.id,
              label: setting.label,
              detail: `Remove documented setting “${setting.label}: ${setting.value}”.`,
            },
          ],
  );
  const settingChangesAndAdditions: PerformanceTransitionItem[] = target.customSettings.flatMap(
    (setting): PerformanceTransitionItem[] => {
      const existing = currentSettings.get(setting.id);
      if (!existing) {
        return [
          {
            id: `transition:setting-add:${setting.id}`,
            kind: "setting" as const,
            action: "add" as const,
            targetId: setting.id,
            label: setting.label,
            detail: `Add documented setting “${setting.label}: ${setting.value}”.`,
          },
        ];
      }
      if (
        existing.label === setting.label &&
        existing.value === setting.value &&
        existing.category === setting.category
      ) {
        return [];
      }
      return [
        {
          id: `transition:setting-set:${setting.id}`,
          kind: "setting" as const,
          action: "set" as const,
          targetId: setting.id,
          label: setting.label,
          detail: `Change “${existing.label}: ${existing.value}” to “${setting.label}: ${setting.value}”.`,
        },
      ];
    },
  );

  const cableRemovals: PerformanceTransitionItem[] = removals.map((cable) => ({
    id: `transition:cable-remove:${cable.id}`,
    kind: "cable",
    action: "remove",
    targetId: cable.id,
    revealTargetId: firstPortId(cable),
    label: cable.label
      ? `Remove cable ${cable.number} — ${cable.label}`
      : `Remove cable ${cable.number}`,
    detail: describeCable(cable),
    colour: cable.colour,
  }));
  const cableAdditions: PerformanceTransitionItem[] = additions.map((cable) => ({
    id: `transition:cable-add:${cable.id}`,
    kind: "cable",
    action: "add",
    targetId: cable.id,
    revealTargetId: firstPortId(cable),
    label: "Add patch cable",
    detail: describeCable(cable),
    colour: cable.colour,
  }));

  return [
    ...cableRemovals,
    ...controlItems,
    ...settingRemovals,
    ...settingChangesAndAdditions,
    ...cableAdditions,
  ];
}
